import { Box, Stack } from "@mui/material";
import { ColDef, ICellRendererParams } from "ag-grid-enterprise";

import { SupplierChip } from "@/shared/components/SupplierChip";
import { getMomentTz } from "@dexteel/mesf-core";
import { AcknowledgeButton } from "../components/AcknowledgeButton";
import { EvaluationChips } from "../components/EvaluationChips";
import { SupplierHeatRow } from "../models/ChemistryDraft";

const EvaluationCell = ({ data }: ICellRendererParams<SupplierHeatRow>) =>
	data ? <EvaluationChips heat={data} compact /> : null;

const SupplierCell = ({
	data,
	value,
}: ICellRendererParams<SupplierHeatRow>) => (
	<Stack direction="row" spacing={0.75} alignItems="center">
		<Box
			sx={{
				width: 8,
				height: 8,
				flexShrink: 0,
				borderRadius: "50%",
				bgcolor: data?.AcknowledgedAt == null ? "primary.main" : "transparent",
			}}
		/>
		{value ? (
			<>
				<SupplierChip value={value} />
				<span>{value}</span>
			</>
		) : (
			<span>-</span>
		)}
	</Stack>
);

const moment = getMomentTz();

// Certificate dates are calendar days (SQL DATE): format them as-is, without a time-zone shift.
export const formatDateOnly = (value: string | null) =>
	value ? moment.utc(value).format("MM/DD/YY") : "";

// 220" or, when the heat ships in bundles of several lengths, 117"–118"
export const formatLength = (heat?: SupplierHeatRow | null) => {
	if (heat?.LengthMin == null) return "";
	const min = `${Number(heat.LengthMin)}"`;
	return heat.LengthMax == null || heat.LengthMax === heat.LengthMin
		? min
		: `${min}–${Number(heat.LengthMax)}"`;
};

const startAlignedCellStyle = {
	display: "flex",
	alignItems: "center",
	justifyContent: "flex-start",
};

export const useSupplierHeatsTableData = ({
	canAcknowledge,
	isAcknowledging,
	onToggleAcknowledge,
}: {
	canAcknowledge: boolean;
	isAcknowledging: boolean;
	onToggleAcknowledge: (heat: SupplierHeatRow) => void;
}) => {
	const columnDefs: ColDef<SupplierHeatRow>[] = [
		{
			field: "Supplier",
			headerName: "Supplier",
			cellRenderer: SupplierCell,
			cellStyle: { ...startAlignedCellStyle, paddingLeft: 8 },
			width: 150,
			flex: 0,
			sortable: true,
		},
		{
			field: "ReportDate",
			headerName: "Report Date",
			valueFormatter: (params) =>
				formatDateOnly((params.data as SupplierHeatRow).ReportDate),
			width: 110,
			flex: 0,
		},
		{
			field: "HeatName",
			headerName: "Heat",
			width: 100,
			flex: 0,
		},
		{
			field: "ExternalHeatNumber",
			headerName: "Supplier Heat",
			width: 120,
			flex: 0,
		},
		{
			field: "Grade",
			headerName: "Grade",
			valueGetter: (params) => params.data?.Grade ?? "",
			width: 90,
			flex: 0,
		},
		{
			field: "GradeDescription",
			headerName: "Description",
			minWidth: 140,
			flex: 1,
		},
		{
			field: "EvaluationCode",
			headerName: "Chemistry",
			cellRenderer: EvaluationCell,
			cellStyle: startAlignedCellStyle,
			minWidth: 190,
			flex: 2,
		},
		{
			field: "Product",
			headerName: "Product",
			minWidth: 140,
			flex: 1,
		},
		{
			field: "Size",
			headerName: "Size",
			width: 80,
			flex: 0,
		},
		{
			colId: "Length",
			headerName: "Length",
			valueGetter: (params) => formatLength(params.data),
			comparator: (_a, _b, nodeA, nodeB) =>
				(nodeA.data?.LengthMin ?? -1) - (nodeB.data?.LengthMin ?? -1),
			width: 100,
			flex: 0,
		},
		{
			field: "FileName",
			headerName: "Document",
			minWidth: 160,
			flex: 1,
			tooltipField: "FileName",
			cellStyle: startAlignedCellStyle,
		},
		{
			colId: "Acknowledged",
			headerName: "",
			width: 50,
			flex: 0,
			cellRenderer: ({ data }: ICellRendererParams<SupplierHeatRow>) =>
				data ? (
					<AcknowledgeButton
						heat={data}
						canAcknowledge={canAcknowledge}
						isAcknowledging={isAcknowledging}
						onToggleAcknowledge={onToggleAcknowledge}
						testId={`acknowledge-heat-${data.ReportHeatId}`}
					/>
				) : null,
		},
	];

	return { columnDefs };
};
